/**
 * International Draughts (Nigerian Draft) Engine — 10×10
 *
 * Rules implemented:
 *  1. Capturing is compulsory (forward AND backward for men).
 *  2. When multiple capture paths exist the player is FREE to choose any
 *     (longest-capture rule is NOT enforced).
 *  3. A man reaching the opponent's back rank is crowned King.
 *  4. Kings are "flying kings" — they slide any number of empty squares
 *     diagonally and can turn corners during a multi-capture sequence.
 *     After capturing the last piece the king may land on ANY empty square
 *     along that same diagonal behind the captured piece.
 *  5. A piece may NOT jump the same opponent piece twice in one turn.
 *  6. Captured pieces are removed only after the full capture sequence.
 *     (For simplicity we remove immediately but mark them so they cannot
 *      be jumped again.)
 *
 * Board representation:
 *   10×10 array.  Playable dark squares where (row+col)%2 === 1.
 *   'w' = white man, 'W' = white king, 'b' = black man, 'B' = black king.
 *   White starts rows 6-9 (bottom), Black starts rows 0-3 (top).
 *   White moves toward row 0, Black toward row 9.
 */

export const BOARD_SIZE = 10;
const DARK_SQUARE = 1;

function cloneBoard(board) {
  return board.map((row) => row.slice());
}

/* ── Board setup ─────────────────────────────────────────── */

export function createInitialBoard() {
  const board = Array.from({ length: BOARD_SIZE }, () => Array(BOARD_SIZE).fill(null));
  for (let row = 0; row < BOARD_SIZE; row += 1) {
    for (let col = 0; col < BOARD_SIZE; col += 1) {
      if ((row + col) % 2 !== DARK_SQUARE) continue;
      if (row <= 3) board[row][col] = 'b';  // black men top 4 rows
      if (row >= 6) board[row][col] = 'w';  // white men bottom 4 rows
    }
  }
  return board;
}

/* ── Notation helpers (a1 – j10) ─────────────────────────── */

export function parseSquare(square) {
  const text = String(square || '').trim().toLowerCase();
  const match = /^([a-j])(10|[1-9])$/.exec(text);
  if (!match) return null;
  const col = match[1].charCodeAt(0) - 'a'.charCodeAt(0);
  const rowFromBottom = Number(match[2]) - 1;
  const row = (BOARD_SIZE - 1) - rowFromBottom;
  return { row, col };
}

export function formatSquare(row, col) {
  const file = String.fromCharCode('a'.charCodeAt(0) + col);
  const rank = String(BOARD_SIZE - row);
  return `${file}${rank}`;
}

function isInside(row, col) {
  return row >= 0 && row < BOARD_SIZE && col >= 0 && col < BOARD_SIZE;
}

/* ── Piece helpers ───────────────────────────────────────── */

function pieceColor(piece) {
  if (!piece) return null;
  const p = String(piece).toLowerCase();
  if (p === 'w') return 'w';
  if (p === 'b') return 'b';
  return null;
}

function isKing(piece) {
  return piece === 'W' || piece === 'B';
}

function shouldPromote(piece, row) {
  if (!piece) return false;
  if (piece === 'w' && row === 0) return true; // white reaches top
  if (piece === 'b' && row === BOARD_SIZE - 1) return true; // black reaches bottom
  return false;
}

function promote(piece) {
  if (piece === 'w') return 'W';
  if (piece === 'b') return 'B';
  return piece;
}

const DIAG_DIRS = [[-1, -1], [-1, 1], [1, -1], [1, 1]];

/* ── Simple (non-capture) moves ──────────────────────────── */

function getSimpleMovesForPiece(board, row, col) {
  const piece = board[row][col];
  if (!piece) return [];
  const color = pieceColor(piece);
  const king = isKing(piece);
  const out = [];

  if (king) {
    // Flying king: slide any number of empty squares diagonally
    for (const [dr, dc] of DIAG_DIRS) {
      let r = row + dr;
      let c = col + dc;
      while (isInside(r, c) && board[r][c] === null) {
        out.push({
          from: formatSquare(row, col),
          to: formatSquare(r, c),
          captures: [],
        });
        r += dr;
        c += dc;
      }
    }
  } else {
    // Men: forward only 1 step.  White goes toward row 0, black toward row 9.
    const forwardDirs = color === 'w'
      ? [[-1, -1], [-1, 1]]
      : [[1, -1], [1, 1]];
    for (const [dr, dc] of forwardDirs) {
      const nr = row + dr;
      const nc = col + dc;
      if (!isInside(nr, nc)) continue;
      if (board[nr][nc] !== null) continue;
      out.push({
        from: formatSquare(row, col),
        to: formatSquare(nr, nc),
        captures: [],
      });
    }
  }

  return out;
}

/* ── Capture moves ───────────────────────────────────────── */

/**
 * For men: can capture 1 square forward or backward by jumping over
 * an adjacent opponent piece to the empty square behind.
 *
 * For kings (flying): travel along a diagonal, find an opponent piece
 * with at least 1 empty square behind it, and land on ANY empty square
 * along the continuation of that diagonal.
 *
 * `alreadyCaptured` tracks squares whose pieces have been captured
 * earlier in this multi-jump turn and therefore cannot be jumped again.
 */
function getCaptureMovesForPiece(board, row, col, alreadyCaptured = new Set()) {
  const piece = board[row][col];
  if (!piece) return [];
  const color = pieceColor(piece);
  const king = isKing(piece);
  const out = [];

  if (king) {
    // Flying king capture
    for (const [dr, dc] of DIAG_DIRS) {
      let r = row + dr;
      let c = col + dc;
      // Slide over empty squares until we hit something
      while (isInside(r, c) && board[r][c] === null) {
        r += dr;
        c += dc;
      }
      // Check if it's an opponent piece we haven't already captured
      if (!isInside(r, c)) continue;
      const overPiece = board[r][c];
      if (!overPiece) continue;
      if (pieceColor(overPiece) === color) continue;
      const overSq = formatSquare(r, c);
      if (alreadyCaptured.has(overSq)) continue;

      // Land on any empty square behind the captured piece
      let lr = r + dr;
      let lc = c + dc;
      while (isInside(lr, lc) && board[lr][lc] === null) {
        out.push({
          from: formatSquare(row, col),
          to: formatSquare(lr, lc),
          captures: [overSq],
        });
        lr += dr;
        lc += dc;
      }
    }
  } else {
    // Man capture: forward and backward (diag ±1 over, ±2 land)
    for (const [dr, dc] of DIAG_DIRS) {
      const overRow = row + dr;
      const overCol = col + dc;
      const nr = row + dr * 2;
      const nc = col + dc * 2;
      if (!isInside(nr, nc) || !isInside(overRow, overCol)) continue;

      const overPiece = board[overRow][overCol];
      if (!overPiece) continue;
      if (pieceColor(overPiece) === color) continue;
      const overSq = formatSquare(overRow, overCol);
      if (alreadyCaptured.has(overSq)) continue;
      if (board[nr][nc] !== null) continue;

      out.push({
        from: formatSquare(row, col),
        to: formatSquare(nr, nc),
        captures: [overSq],
      });
    }
  }

  return out;
}

/* ── Legal moves ─────────────────────────────────────────── */

export function getLegalMoves(board, turnColor, forcedSquare = null, alreadyCaptured = []) {
  const capturedSet = new Set(alreadyCaptured);
  const allCaptures = [];
  const allSimple = [];

  for (let row = 0; row < BOARD_SIZE; row += 1) {
    for (let col = 0; col < BOARD_SIZE; col += 1) {
      const piece = board[row][col];
      if (!piece || pieceColor(piece) !== turnColor) continue;
      const sq = formatSquare(row, col);
      if (forcedSquare && sq !== forcedSquare) continue;

      const captures = getCaptureMovesForPiece(board, row, col, capturedSet);
      if (captures.length) allCaptures.push(...captures);
      else if (!forcedSquare) allSimple.push(...getSimpleMovesForPiece(board, row, col));
    }
  }

  // Capturing is compulsory
  if (allCaptures.length) return allCaptures;
  return forcedSquare ? [] : allSimple;
}

/* ── Apply a single step of a move ───────────────────────── */

export function applyMove(boardInput, turnColor, from, to, forcedSquare = null, alreadyCaptured = []) {
  const board = cloneBoard(boardInput);
  const fromPos = parseSquare(from);
  const toPos = parseSquare(to);

  if (!fromPos || !toPos) return { ok: false, error: 'Invalid square' };
  if (!isInside(fromPos.row, fromPos.col) || !isInside(toPos.row, toPos.col)) {
    return { ok: false, error: 'Invalid square' };
  }

  const piece = board[fromPos.row][fromPos.col];
  if (!piece || pieceColor(piece) !== turnColor) {
    return { ok: false, error: 'Invalid piece' };
  }

  if (forcedSquare && formatSquare(fromPos.row, fromPos.col) !== forcedSquare) {
    return { ok: false, error: `You must continue from ${forcedSquare}` };
  }

  const legalMoves = getLegalMoves(board, turnColor, forcedSquare, alreadyCaptured);
  const selected = legalMoves.find((m) => m.from === from && m.to === to);
  if (!selected) {
    return { ok: false, error: 'Illegal move' };
  }

  // Move piece
  board[fromPos.row][fromPos.col] = null;
  let movedPiece = piece;

  // Process captures
  let didCapture = false;
  const newAlreadyCaptured = [...alreadyCaptured];
  if (selected.captures.length > 0) {
    didCapture = true;
    for (const capturedSquare of selected.captures) {
      const pos = parseSquare(capturedSquare);
      if (pos) {
        board[pos.row][pos.col] = null;
        newAlreadyCaptured.push(capturedSquare);
      }
    }
  }

  // Promotion: a man reaching the back rank becomes a king
  const promotedNow = shouldPromote(movedPiece, toPos.row) && !isKing(movedPiece);
  if (promotedNow) {
    movedPiece = promote(movedPiece);
  }

  board[toPos.row][toPos.col] = movedPiece;

  // Determine next state
  let nextTurn = turnColor === 'w' ? 'b' : 'w';
  let mustContinueFrom = null;

  // Multi-capture continuation: if we captured and there are more captures
  // available from the landing square, the player MUST continue.
  // EXCEPTION: if the man was JUST promoted, the turn ends (Nigerian rule).
  if (didCapture && !promotedNow) {
    const followUps = getCaptureMovesForPiece(board, toPos.row, toPos.col, new Set(newAlreadyCaptured));
    if (followUps.length > 0) {
      nextTurn = turnColor;
      mustContinueFrom = formatSquare(toPos.row, toPos.col);
    }
  }

  return {
    ok: true,
    board,
    move: {
      from,
      to,
      captures: selected.captures,
      promoted: promotedNow,
      piece,
      pieceAfter: movedPiece,
    },
    didCapture,
    promotedNow,
    nextTurn,
    mustContinueFrom,
    alreadyCaptured: newAlreadyCaptured,
  };
}

/* ── Win / draw detection ────────────────────────────────── */

export function countPieces(board) {
  let white = 0;
  let black = 0;
  for (let row = 0; row < BOARD_SIZE; row += 1) {
    for (let col = 0; col < BOARD_SIZE; col += 1) {
      const piece = board[row][col];
      if (!piece) continue;
      if (pieceColor(piece) === 'w') white += 1;
      if (pieceColor(piece) === 'b') black += 1;
    }
  }
  return { white, black };
}

export function isGameOver(board, turnColor, noCaptureHalfmoves = 0, drawLimitHalfmoves = 80) {
  const pieces = countPieces(board);
  if (pieces.white === 0) return { over: true, winnerColor: 'b', reason: 'no-pieces' };
  if (pieces.black === 0) return { over: true, winnerColor: 'w', reason: 'no-pieces' };

  const legal = getLegalMoves(board, turnColor, null);
  if (!legal.length) {
    const winnerColor = turnColor === 'w' ? 'b' : 'w';
    return { over: true, winnerColor, reason: 'no-legal-moves' };
  }

  if (Number(noCaptureHalfmoves) >= Number(drawLimitHalfmoves)) {
    return { over: true, winnerColor: null, reason: 'draw-no-capture-limit' };
  }

  return { over: false, winnerColor: null, reason: null };
}


// --- BOT LOGIC ---




function countAllPieces(board) {
  let w = 0; let W = 0; let b = 0; let B = 0;
  for (let r = 0; r < board.length; r += 1) {
    for (let c = 0; c < board[r].length; c += 1) {
      const p = board[r][c];
      if (p === 'w') w += 1;
      else if (p === 'W') W += 1;
      else if (p === 'b') b += 1;
      else if (p === 'B') B += 1;
    }
  }
  return { w, W, b, B };
}

export function evaluateBoard(board, botColor) {
  const pieces = countAllPieces(board);
  const oppColor = botColor === 'b' ? 'w' : 'b';

  const botMen = botColor === 'b' ? pieces.b : pieces.w;
  const botKings = botColor === 'b' ? pieces.B : pieces.W;
  const oppMen = oppColor === 'b' ? pieces.b : pieces.w;
  const oppKings = oppColor === 'b' ? pieces.B : pieces.W;

  let score = (botMen * 1 + botKings * 3) - (oppMen * 1 + oppKings * 3);

  for (let r = 0; r < board.length; r += 1) {
    for (let c = 0; c < board[r].length; c += 1) {
      const p = board[r][c];
      if (!p) continue;
      const pc = pieceColor(p);
      const sign = pc === botColor ? 1 : -1;
      const distFromCenter = Math.abs(r - 4.5) + Math.abs(c - 4.5);
      score += sign * (5 - distFromCenter) * 0.05;
      if (!isKing(p)) {
        const advancement = pc === 'b' ? r : (board.length - 1 - r);
        score += sign * advancement * 0.08;
      }
      if (!isKing(p)) {
        if ((pc === 'w' && r === board.length - 1) || (pc === 'b' && r === 0)) {
          score += sign * 0.15;
        }
      }
    }
  }

  return score;
}

export function scoreMove(board, color, move, alreadyCaptured, depth) {
  const result = applyMove(board, color, move.from, move.to, null, alreadyCaptured);
  if (!result.ok) return -999;

  let finalBoard = result.board;
  let cont = result.mustContinueFrom;
  let acc = result.alreadyCaptured || [];

  while (cont) {
    const followups = getLegalMoves(finalBoard, color, cont, acc);
    if (!followups.length) break;
    const pick = followups.reduce((best, candidate) => (
      (candidate.captures || []).length > (best.captures || []).length ? candidate : best
    ), followups[0]);
    const chained = applyMove(finalBoard, color, pick.from, pick.to, cont, acc);
    if (!chained.ok) break;
    finalBoard = chained.board;
    cont = chained.mustContinueFrom;
    acc = chained.alreadyCaptured || [];
  }

  let score = evaluateBoard(finalBoard, color);

  if (depth > 0) {
    const oppColor = color === 'w' ? 'b' : 'w';
    const oppMoves = getLegalMoves(finalBoard, oppColor);
    if (oppMoves.length) {
      let worstForUs = Infinity;
      for (const oppMove of oppMoves) {
        const oppScore = scoreMove(finalBoard, oppColor, oppMove, [], depth - 1);
        const ourScore = -oppScore;
        if (ourScore < worstForUs) worstForUs = ourScore;
      }
      score = worstForUs;
    } else {
      score = 100;
    }
  }

  return score;
}

export function getBotMove(board, color, forcedSquare = null, alreadyCaptured = [], level = 3) {
  const legal = getLegalMoves(board, color, forcedSquare, alreadyCaptured);
  if (!legal.length) return null;

  if (level <= 1) {
    return legal[Math.floor(Math.random() * legal.length)];
  }

  if (level === 2) {
    const captures = legal.filter((m) => (m.captures || []).length > 0);
    const pool = captures.length ? captures : legal;
    const scored = pool.map((m) => ({
      move: m,
      score: scoreMove(board, color, m, alreadyCaptured, 0),
    }));
    scored.sort((a, b) => b.score - a.score);
    const top = scored.slice(0, Math.min(3, scored.length));
    return top[Math.floor(Math.random() * top.length)].move;
  }

  const scored = legal.map((m) => ({
    move: m,
    score: scoreMove(board, color, m, alreadyCaptured, 3),
  }));
  scored.sort((a, b) => b.score - a.score);
  const bestScore = scored[0].score;
  const topMoves = scored.filter((entry) => entry.score >= bestScore - 0.01);
  return topMoves[Math.floor(Math.random() * topMoves.length)].move;
}

